import { useEffect, useState, type FormEvent } from 'react';
import { api, type Note } from '../lib/api';

const NAME_KEY = 'cookbook-note-name';

function formatWhen(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function NotesPanel({ slug }: { slug: string }) {
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [body, setBody] = useState('');
  const [author, setAuthor] = useState(() => localStorage.getItem(NAME_KEY) ?? '');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');

  useEffect(() => {
    let alive = true;
    api
      .notes(slug)
      .then((n) => alive && setNotes(n))
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
  }, [slug]);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!body.trim() || saving) return;
    setSaving(true);
    try {
      localStorage.setItem(NAME_KEY, author.trim());
      const note = await api.addNote(slug, { body: body.trim(), author: author.trim() || undefined });
      setNotes((n) => [note, ...(n ?? [])]);
      setBody('');
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit(id: number) {
    const trimmed = editBody.trim();
    if (!trimmed) return;
    const updated = await api.editNote(id, trimmed);
    setNotes((n) => (n ?? []).map((x) => (x.id === id ? updated : x)));
    setEditingId(null);
  }

  async function remove(id: number) {
    if (!window.confirm('Delete this note? It’s gone for good.')) return;
    await api.deleteNote(id);
    setNotes((n) => (n ?? []).filter((x) => x.id !== id));
  }

  return (
    <div>
      <form onSubmit={add} className="rounded-xl border border-line bg-card p-4">
        <label htmlFor="note-body" className="font-sans text-[0.8rem] font-medium text-ink-soft">
          How did it go?
        </label>
        <textarea
          id="note-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder="Doubled the green onions, cut the sugar a little — better."
          className="mt-1.5 w-full resize-y rounded-lg border border-line bg-paper px-3 py-2.5 font-serif text-[0.98rem] leading-relaxed placeholder:text-ink-soft/60 focus:border-ink-soft focus:outline-none"
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label htmlFor="note-name" className="sr-only">
            Your name (optional)
          </label>
          <input
            id="note-name"
            type="text"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            maxLength={100}
            placeholder="Your name (optional)"
            className="h-11 min-w-0 flex-1 rounded-lg border border-line bg-paper px-3 font-sans text-[0.88rem] placeholder:text-ink-soft/60 focus:border-ink-soft focus:outline-none"
          />
          <button
            type="submit"
            disabled={saving || !body.trim()}
            className="h-11 rounded-full bg-ink px-6 font-sans text-[0.88rem] font-semibold text-paper transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            {saving ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </form>

      {loadError ? (
        <p className="mt-4 font-serif text-ink-soft">
          The notebook didn’t load — check the connection and refresh.
        </p>
      ) : notes === null ? (
        <p className="mt-4 font-sans text-[0.85rem] text-ink-soft">Opening the notebook…</p>
      ) : notes.length === 0 ? (
        <p className="mt-4 font-serif text-ink-soft">
          No notes yet. After you cook it, write how it went — future you says thanks.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {notes.map((note) => (
            <li key={note.id} className="rounded-xl border border-line bg-card p-4">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                {note.author && <span className="font-sans text-[0.85rem] font-semibold">{note.author}</span>}
                <time dateTime={note.created_at} className="font-sans text-[0.75rem] text-ink-soft">
                  {formatWhen(note.created_at)}
                  {note.updated_at !== note.created_at && ' · edited'}
                </time>
                <span className="ml-auto flex gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(note.id);
                      setEditBody(note.body);
                    }}
                    className="rounded px-2 py-1 font-sans text-[0.78rem] font-medium text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ink"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(note.id)}
                    className="rounded px-2 py-1 font-sans text-[0.78rem] font-medium text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ink"
                  >
                    Delete
                  </button>
                </span>
              </div>
              {editingId === note.id ? (
                <div className="mt-2">
                  <label htmlFor={`edit-${note.id}`} className="sr-only">
                    Edit note
                  </label>
                  <textarea
                    id={`edit-${note.id}`}
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={3}
                    maxLength={5000}
                    className="w-full resize-y rounded-lg border border-line bg-paper px-3 py-2.5 font-serif text-[0.98rem] leading-relaxed focus:border-ink-soft focus:outline-none"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => saveEdit(note.id)}
                      className="h-10 rounded-full bg-ink px-5 font-sans text-[0.82rem] font-semibold text-paper hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="h-10 rounded-full border border-line bg-card px-5 font-sans text-[0.82rem] font-medium text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-1.5 whitespace-pre-wrap font-serif leading-relaxed">{note.body}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
